import classNames from "classnames";
import Tag from "components/Tag";
import Tooltip from "components/Tooltip";
import { Paper } from "helpers/api";
import { getAuthorDisplayName } from "helpers/cite";
import { useAppDispatch, useAppSelector } from "hooks/useStore";
import { useCallback } from "react";
import {
  clearSources,
  removeSource,
  selectSources,
} from "store/slices/sources";
import { UploadingTags } from "./UploadingTags";

interface AttachedThreadInteractionSourceProps {
  source: Paper;
  onRemove?: (paperId: string) => void;
  disableRemove?: boolean;
}

/**
 * @component AttachedThreadInteractionSource
 * @description Individual source tag with remove functionality
 */
function AttachedThreadInteractionSource({
  source,
  onRemove,
  disableRemove = false,
}: AttachedThreadInteractionSourceProps) {
  const handleRemove = () => {
    if (!disableRemove) {
      onRemove?.(source.paper_id);
    }
  };

  const author = getAuthorDisplayName(source.authors[0]);

  return (
    <Tooltip
      content={<span className="text-center line-clamp-2">{source.title}</span>}
    >
      <div className="flex">
        <Tag
          key={source.paper_id}
          label={
            <>
              <span className="text-fg-base">{author}</span>
              <span>{source.year}</span>
            </>
          }
          icon="icon-file"
          size="sm"
          dismissable={!disableRemove}
          className="flex gap-1 truncate transition-colors bg-bg-subtle"
          onDismiss={handleRemove}
        />
      </div>
    </Tooltip>
  );
}

interface AttachedThreadInteractionSourcesProps {
  className?: string;
  disableRemove?: boolean;
  sourcesOverride?: Paper[]; // Optional override for sources instead of using app state
}

/**
 * @component AttachedThreadInteractionSources
 * @description Displays the current sources attached to a thread interaction as removable tags.
 * Uses sourcesOverride prop if provided, otherwise falls back to app state sources.
 * */
function AttachedThreadInteractionSources({
  className,
  disableRemove = false,
  sourcesOverride,
}: AttachedThreadInteractionSourcesProps) {
  const dispatch = useAppDispatch();
  const appStateSources = useAppSelector(selectSources);

  // Use override sources if provided, otherwise fall back to app state sources
  const sourcesToDisplay = sourcesOverride ?? appStateSources;

  const handleRemoveSource = useCallback(
    (paperId: string) => {
      dispatch(removeSource(paperId));
    },
    [dispatch]
  );

  const handleClearAll = useCallback(() => {
    dispatch(clearSources());
  }, [dispatch]);

  if (sourcesToDisplay.length === 0) {
    // Still render if there might be an upload happening
    return (
      <div className="flex">
        <UploadingTags />
      </div>
    );
  }

  return (
    <div className={classNames("flex flex-wrap gap-2", className)}>
      {!disableRemove && (
        <Tag
          size="sm"
          label="Clear all"
          className="bg-bg-subtle"
          dismissable
          onClick={handleClearAll}
        />
      )}
      <UploadingTags />
      {sourcesToDisplay.map((source: Paper) => (
        <AttachedThreadInteractionSource
          key={source.paper_id}
          source={source}
          onRemove={handleRemoveSource}
          disableRemove={disableRemove}
        />
      ))}
    </div>
  );
}

export default AttachedThreadInteractionSources;
